import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { PRODUCTS } from './products';
type GoalKey = 'Recovery' | 'Weight management' | 'Performance' | 'Skin & Hair' | 'Longevity' | 'Sleep';
const GOALS: Array<{
  key: GoalKey;
  label: string;
  description: string;
  slug: string;
}> = [{
  key: 'Recovery',
  label: 'Recovery & healing',
  description: 'Tissue repair, joint, gut',
  slug: 'recovery'
}, {
  key: 'Weight management',
  label: 'Weight management',
  description: 'Metabolic & GLP-1 research',
  slug: 'weight-management'
}, {
  key: 'Performance',
  label: 'Performance',
  description: 'Strength, endurance, GH axis',
  slug: 'performance'
}, {
  key: 'Skin & Hair',
  label: 'Skin & hair',
  description: 'Cosmetic & dermal research',
  slug: 'skin-hair'
}, {
  key: 'Longevity',
  label: 'Longevity',
  description: 'Anti-aging, cellular research',
  slug: 'longevity'
}, {
  key: 'Sleep',
  label: 'Sleep & stress',
  description: 'Recovery & neuro research',
  slug: 'sleep'
}];
export function GoalsGrid() {
  return <section className="w-full py-20 lg:py-28 bg-secondary/30 border-y border-border" data-id="element-226">
      <div className="container mx-auto" data-id="element-227">
        <div className="grid lg:grid-cols-[1fr_1.8fr] gap-10 lg:gap-20" data-id="element-228">
          {/* Left: editorial intro */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} className="lg:sticky lg:top-24 lg:self-start" data-id="element-229">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-4" data-id="element-230">
              Shop by research goal
            </p>
            <h2 className="text-3xl lg:text-[44px] font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-231">
              Start with what you're researching
              <span className="text-primary" data-id="element-232">.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground max-w-md" data-id="element-233">
              Every peptide in our catalogue is independently verified and ships
              same-day from the UK. Browse by the research area you're working
              in.
            </p>
            <Link to="/shop" className="inline-flex items-center gap-1.5 mt-6 text-[13px] font-medium text-foreground hover:text-primary transition-colors" data-id="element-234">
              View full catalogue
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} data-id="element-235" />
            </Link>
          </motion.div>

          {/* Right: goal list — editorial menu, not cards */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="rounded-2xl border border-border bg-card overflow-hidden" data-id="element-236">
            <ul className="divide-y divide-border" data-id="element-237">
              {GOALS.map((goal, i) => {
              const matches = PRODUCTS.filter(p => p.goals.includes(goal.key));
              const featured = matches[0];
              const minPrice = matches.length > 0 ? Math.min(...matches.map(p => p.price)) : null;
              return <li key={goal.key} data-id="element-238">
                    <Link to={`/shop/category/${goal.slug}`} className="group flex items-center gap-5 px-6 lg:px-8 py-6 lg:py-7 hover:bg-secondary/40 transition-colors" data-id="element-239">
                      {/* Number */}
                      <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground font-medium w-7 flex-shrink-0" data-id="element-240">
                        0{i + 1}
                      </span>

                      {/* Goal */}
                      <div className="flex-1 min-w-0" data-id="element-241">
                        <div className="flex items-baseline gap-3 flex-wrap" data-id="element-242">
                          <h3 className="text-[20px] lg:text-[22px] font-semibold tracking-tight text-foreground leading-tight group-hover:text-primary transition-colors" data-id="element-243">
                            {goal.label}
                          </h3>
                          <span className="font-mono text-[11px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-244">
                            {matches.length}{' '}
                            {matches.length === 1 ? 'peptide' : 'peptides'}
                          </span>
                        </div>
                        <p className="text-[13px] text-muted-foreground mt-1" data-id="element-245">
                          {goal.description}
                          {featured && <>
                              <span className="text-border mx-2" data-id="element-246">·</span>
                              Including{' '}
                              <span className="text-foreground font-medium" data-id="element-247">
                                {featured.name}
                              </span>
                            </>}
                        </p>
                      </div>

                      {/* Price */}
                      {minPrice !== null && <div className="hidden sm:block text-right whitespace-nowrap" data-id="element-248">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-249">
                            From
                          </div>
                          <div className="font-mono text-[15px] font-medium text-foreground mt-0.5" data-id="element-250">
                            £{minPrice}
                          </div>
                        </div>}

                      {/* Arrow */}
                      <ArrowUpRight className="h-5 w-5 text-muted-foreground flex-shrink-0 transition-all group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} data-id="element-251" />
                    </Link>
                  </li>;
            })}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}